import {
    IonBadge,
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    IonTitle,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter,
    useIonToast,
    useIonViewWillEnter
} from "@ionic/react";
import {
    addOutline,
    americanFootballOutline,
    arrowForwardOutline,
    copyOutline,
    logOutOutline,
    peopleOutline,
    refreshOutline,
    ticketOutline,
    trophyOutline,
} from "ionicons/icons";

import {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import {
    getAuthenticatedUserId,
    getAuthenticatedUsername,
    isAuthenticated,
    logoutFantasy
} from "../services/auth";

import {
    fantasyApiGet,
    fantasyApiPost
} from "../services/fantasyApi";

import {
    fantasyPushRegisterServiceWorker
} from "../services/fantasyPushNotifications";

import FantasyPushSettings from
    "../components/FantasyPushSettings";

import ListContainer from "../components/FantasyContainer";
import "./Fantasy.css";

interface FantasyLeague {
    id: string;
    name: string;
}

function getDisplayName(): string {
    return getAuthenticatedUsername() ||
        "Jugador";
}

const Fantasy: React.FC = () => {
    const router = useIonRouter();
    const [present] = useIonToast();

    const [leagues, setLeagues] = useState<FantasyLeague[]>([]);
    const [leagueId, setLeagueId] = useState("");

    const [loading, setLoading] = useState(true);
    const [joiningLeague, setJoiningLeague] = useState(false);
    const [error, setError] = useState("");

    const [loggingOut, setLoggingOut] = useState(false);

const loggingOutRef = useRef(false);

const leaguesRequestRef = useRef<
    AbortController | null
>(null);

useIonViewWillEnter(
    function () {
        if (!isAuthenticated()) {
            router.push(
                "/home",
                "root",
                "replace"
            );

            return;
        }

        loggingOutRef.current = false;
        setLoggingOut(false);
    }
);

    function showToast(
        message: string,
        color: "success" | "danger" | "warning"
    ) {
        present({
            message,
            duration: 2200,
            position: "bottom",
            color,
            buttons: [
                {
                    text: "Cerrar",
                    role: "cancel"
                }
            ]
        });
    }

const loadLeagues = useCallback(
    async function (_forceRefresh = false) {
        if (loggingOutRef.current) {
            return;
        }

        if (!isAuthenticated()) {
            return;
        }

        /*
            Conservamos el contador para ignorar resultados viejos
            si el usuario cierra sesión durante una carga.
        */
        leaguesRequestRef.current?.abort();

        const requestController = new AbortController();

        leaguesRequestRef.current = requestController;

        try {
            setError("");

            setLoading(true);

            const result = await fantasyApiGet<{
                success: boolean;
                leagues: FantasyLeague[];
            }>(
                "control_MyFantasyLeagues.php"
            );

            if (loggingOutRef.current) {
                return;
            }

            setLeagues(
                Array.isArray(result.leagues)
                    ? result.leagues
                    : []
            );
        } catch (requestError) {
            if (loggingOutRef.current) {
                return;
            }

            console.error(
                "No fue posible cargar las ligas Fantasy:",
                requestError
            );

            setError(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible cargar tus ligas. Intenta actualizar nuevamente."
            );
        } finally {
            if (
                leaguesRequestRef.current === requestController
            ) {
                leaguesRequestRef.current = null;
            }

            if (!loggingOutRef.current) {
                setLoading(false);
            }
        }
    },
    []
);

    useEffect(
        function () {
            loadLeagues();
        },
        [loadLeagues]
    );

    useEffect(function () {
        fantasyPushRegisterServiceWorker()
            .catch(function (error: unknown) {
                console.warn(
                    "No fue posible registrar el Service Worker:",
                    error
                );
            });
    }, []);


    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadLeagues(true);
        event.detail.complete();
    }

async function joinLeague() {
    const normalizedLeagueId = leagueId.trim();

    if (!normalizedLeagueId) {
        showToast(
            "Ingresa el identificador de la liga que deseas unirte.",
            "warning"
        );

        return;
    }

    if (!isAuthenticated()) {
        showToast(
            "Tu sesión expiró. Inicia sesión nuevamente.",
            "danger"
        );

        router.push(
            "/home",
            "root",
            "replace"
        );

        return;
    }

    try {
        setJoiningLeague(true);

        const teamName =
            getDisplayName().trim() || "Mi equipo";

        const result = await fantasyApiPost<{
            success: boolean;
            message?: string;
        }>(
            "control_newTeam.php",
            {
                leagueId: normalizedLeagueId,
                teamName1: teamName
            }
        );

        setLeagueId("");

        showToast(
            result.message ||
                "Te uniste correctamente a la liga.",
            "success"
        );

        await loadLeagues(true);
    } catch (requestError: unknown) {
        console.error(
            "No fue posible unirse a la liga:",
            requestError
        );

        showToast(
            requestError instanceof Error
                ? requestError.message
                : "No fue posible unirse a la liga.",
            "danger"
        );
    } finally {
        setJoiningLeague(false);
    }
}

async function copyMyUserId(): Promise<void> {
    const userId = getAuthenticatedUserId();

    if (!userId) {
        showToast(
            "No fue posible obtener tu identificador. Inicia sesión nuevamente.",
            "danger"
        );

        return;
    }

    try {
        /*
            navigator.clipboard funciona en HTTPS y navegadores modernos.
        */
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(userId);
        } else {
            /*
                Compatibilidad con navegadores o WebViews antiguos.
            */
            const temporaryInput = document.createElement("textarea");

            temporaryInput.value = userId;
            temporaryInput.setAttribute("readonly", "");
            temporaryInput.style.position = "fixed";
            temporaryInput.style.opacity = "0";

            document.body.appendChild(temporaryInput);

            temporaryInput.select();

            const copied = document.execCommand("copy");

            document.body.removeChild(temporaryInput);

            if (!copied) {
                throw new Error("No se pudo copiar el identificador.");
            }
        }

        showToast(
            "Tu ID fue copiado. Compártelo con el comisionado.",
            "success"
        );
    } catch (copyError: unknown) {
        console.error(
            "No fue posible copiar el ID del usuario:",
            copyError
        );

        showToast(
            "No fue posible copiar tu ID automáticamente.",
            "danger"
        );
    }
}

async function logOut(): Promise<void> {
    if (loggingOutRef.current) {
        return;
    }

    loggingOutRef.current = true;
    setLoggingOut(true);

    /*
        Evita que una respuesta pendiente de fetch actualice
        Fantasy después de que el usuario cerró sesión.
    */
    leaguesRequestRef.current?.abort();
    leaguesRequestRef.current = null;

    setLeagues([]);
    setLeagueId("");
    setError("");

/*
    Cierra la sesión PHP segura y limpia estado local.
*/
await logoutFantasy();

    /*
        history.replace cambia la ruta de forma directa.
        No deja Fantasy en el historial y evita que el usuario
        vuelva a esta pantalla mediante el botón atrás.
    */
    router.push(
        "/home",
        "root",
        "replace"
    );
}

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="fantasy-toolbar">
                    <IonTitle>Fantasy</IonTitle>

<IonButton
    slot="end"
    fill="clear"
    color="medium"
    aria-label="Copiar mi ID de usuario"
    title="Copiar mi ID"
    disabled={loggingOut}
    onClick={copyMyUserId}
>
    <IonIcon icon={copyOutline} />
</IonButton>

<IonButton
    slot="end"
    fill="clear"
    color="medium"
    aria-label="Cerrar sesión"
    disabled={joiningLeague || loggingOut}
    onClick={function () {
        void logOut();
    }}
>
    <IonIcon icon={logOutOutline} />
</IonButton>


                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="fantasy-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando tus ligas..."
                    />
                </IonRefresher>

                <section className="fantasy-hero">
                    <div className="fantasy-hero__icon">
                        <IonIcon icon={americanFootballOutline} />
                    </div>

                    <div className="fantasy-hero__content">
                        <span>FANTASY MEXFBA</span>
                        <h1>Hola, {getDisplayName()}</h1>
                        <p>
                            Gestiona tus equipos, ligas y enfrentamientos.
                        </p>
                    </div>
                </section>
                <section className="fantasy-summary">
                    <div>
                        <IonIcon icon={trophyOutline} />
                        <span>LIGAS ACTIVAS</span>
                        <strong>{leagues.length}</strong>
                    </div>

                    <div>
                        <IonIcon icon={peopleOutline} />
                        <span>ESTADO</span>
                        <strong>Activo</strong>
                    </div>
                </section>
                <section className="fantasy-section fantasy-section--leagues">
                    <div className="fantasy-section__heading">
                        <div>
                            <span>MIS LIGAS</span>
                            <h2>Competencias activas</h2>
                        </div>

                        {!loading && !error && (
                            <IonBadge className="fantasy-count">
                                {leagues.length}
                            </IonBadge>
                        )}
                    </div>

                    {loading && (
                        <IonProgressBar
                            className="fantasy-progress"
                            type="indeterminate"
                        />
                    )}

                    {!loading && error && (
                        <div className="fantasy-empty">
                            <IonIcon icon={refreshOutline} />
                            <strong>No pudimos cargar tus ligas</strong>
                            <span>{error}</span>

                            <IonButton
                                fill="outline"
                                size="small"
                                onClick={function () {
                                    loadLeagues(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    )}

                    {!loading && !error && leagues.length === 0 && (
                        <div className="fantasy-empty">
                            <IonIcon icon={trophyOutline} />
                            <strong>Aún no tienes ligas</strong>
                            <span>
                                Crea una liga o únete con un identificador para
                                comenzar a jugar.
                            </span>
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        leagues.map(function (league) {
                            return (
                                <div
                                    className="fantasy-league"
                                    key={league.id}
                                >
                                    {ListContainer(
                                        league.id,
                                        "https://logowik.com/content/uploads/images/american-football546.logowik.com.webp",
                                        league.name
                                    )}
                                </div>
                            );
                        })}
                </section>
                <section className="fantasy-section">
                    <div className="fantasy-section__heading">
                        <div>
                            <span>COMIENZA A JUGAR</span>
                            <h2>Tu próxima liga</h2>
                        </div>

                        <IonIcon icon={americanFootballOutline} />
                    </div>

                    <div className="fantasy-actions">
                        <button
                            className="fantasy-action fantasy-action--create"
                            type="button"
                            onClick={function () {
                                router.push(
                                    "/createLeague",
                                    "forward",
                                    "push"
                                );
                            }}
                        >
                            <span className="fantasy-action__icon">
                                <IonIcon icon={addOutline} />
                            </span>

                            <span>
                                <strong>Crear una liga</strong>
                                <small>
                                    Invita a tus amigos y define reglas.
                                </small>
                            </span>

                            <IonIcon
                                className="fantasy-action__arrow"
                                icon={arrowForwardOutline}
                            />
                        </button>
<section className="fantasy-user-id">
    <span className="fantasy-user-id__icon">
        <IonIcon icon={copyOutline} />
    </span>

    <div className="fantasy-user-id__content">
        <span>IDENTIFICADOR DE MANAGER</span>

        <strong>¿Te transferirán un equipo?</strong>

        <small>
            Copia tu ID y compártelo con el comisionado de la liga.
        </small>
    </div>

    <IonButton
        className="fantasy-user-id__button"
        fill="outline"
        size="small"
        disabled={loggingOut}
        onClick={copyMyUserId}
    >
        Copiar
    </IonButton>
</section>
                        <div className="fantasy-join">
                            <div className="fantasy-join__heading">
                                <IonIcon icon={ticketOutline} />

                                <div>
                                    <strong>Unirse a una liga</strong>
                                    <span>
                                        Usa el identificador compartido por el
                                        comisionado.
                                    </span>
                                </div>
                            </div>

                            <IonItem
                                className="fantasy-join__input"
                                lines="none"
                            >
                                <IonLabel position="stacked">
                                    ID de liga
                                </IonLabel>

                                <IonInput
                                    value={leagueId}
                                    disabled={joiningLeague}
                                    placeholder="Ejemplo: LIGA-2026"
                                    onIonChange={function (event) {
                                        setLeagueId(
                                            event.detail.value || ""
                                        );
                                    }}
                                />
                            </IonItem>

                            <IonButton
                                className="fantasy-join__button"
                                expand="block"
                                fill="outline"
                                disabled={joiningLeague || loggingOut}
                                onClick={joinLeague}
                            >
                                {joiningLeague ? (
                                    <>
                                        <IonSpinner name="crescent" />
                                        Uniéndose...
                                    </>
                                ) : (
                                    <>
                                        Unirme a la liga
                                        <IonIcon
                                            slot="end"
                                            icon={arrowForwardOutline}
                                        />
                                    </>
                                )}
                            </IonButton>
                        </div>
                    </div>
<FantasyPushSettings />
                </section>
            </IonContent>
        </IonPage>
    );
};

export default Fantasy;
